import { useMemo, useRef } from 'react';
import { getViewportLabel } from 'libs.irb.common';
// Custom hook to create stable reference based on status changes
export const useStablePackages = (packages) => {
    const previousPackagesRef = useRef({});
    const statusHash = useMemo(() => {
        const hash = Object.keys(packages)
            .map((key) => `${key}:${packages[key]?.status}:${packages[key]?.url}:${packages[key]?.errorMessage}`)
            .sort()
            .join('|');
        return hash;
    }, [packages]);
    const previousStatusHashRef = useRef('');
    // Only update the stable reference if status has changed
    if (statusHash !== previousStatusHashRef.current) {
        previousStatusHashRef.current = statusHash;
        previousPackagesRef.current = packages;
    }
    return previousPackagesRef.current;
};
// Custom hook for stable data processing that preserves object identity
export const useStableData = (packages, stableTranslations, translate, study) => {
    const previousDataRef = useRef({});
    const previousProcessedDataRef = useRef([]);
    return useMemo(() => {
        const newDataMap = {};
        let hasChanges = false;
        // Process each package
        Object.keys(packages).forEach((key) => {
            const irbPackage = packages[key];
            if (!irbPackage) {
                return;
            }
            const { status, locale, user, createdAt, buildVersion, modality, viewport, isCoverPageIncluded, url, errorMessage, } = irbPackage;
            // Create a hash for this specific item to detect changes
            const itemHash = `${key}:${status}:${url}:${errorMessage}:${buildVersion}:${locale}`;
            const previousItem = previousDataRef.current[key];
            // If item hasn't changed, reuse the previous object reference
            if (previousItem && previousItem.itemHash === itemHash) {
                newDataMap[key] = previousItem;
            }
            else {
                // Item changed, create new object
                hasChanges = true;
                const options = [translate(getViewportLabel(viewport, modality))];
                if (!isCoverPageIncluded) {
                    options.push(stableTranslations.noCover);
                }
                const fileName = `${(study.displayName || study.name).replace(/\s+/g, '-')}_${buildVersion}_${locale}_${createdAt?.toDate().toISOString()}.pdf`;
                newDataMap[key] = {
                    id: key,
                    status,
                    locale,
                    createdAt: createdAt?.toDate(),
                    user,
                    buildVersion,
                    options,
                    fileName,
                    action: {
                        url,
                        errorMessage,
                    },
                    itemHash, // Store hash for comparison
                };
            }
        });
        // If no changes and array length is same, return previous array reference
        if (!hasChanges && Object.keys(newDataMap).length === previousProcessedDataRef.current.length) {
            return previousProcessedDataRef.current;
        }
        // Update the reference
        previousDataRef.current = newDataMap;
        // Sort and return new array only if there are changes
        const sortedData = Object.values(newDataMap).sort((a, b) => {
            // Primary sort: creation date (most recent first)
            if (a.createdAt && b.createdAt) {
                const dateCompare = b.createdAt.getTime() - a.createdAt.getTime();
                if (dateCompare !== 0) {
                    return dateCompare;
                }
            }
            // Secondary sort: locale
            if (a?.locale && b?.locale) {
                return a.locale.localeCompare(b.locale);
            }
            return 0;
        });
        previousProcessedDataRef.current = sortedData;
        return sortedData;
    }, [packages, stableTranslations, translate, study]);
};
// Hook to create stable translate function
export const useStableTranslate = (translate) => {
    const translateRef = useRef(translate);
    const stableTranslateRef = useRef(null);
    // Only update if translate function actually changed
    if (translateRef.current !== translate) {
        translateRef.current = translate;
        stableTranslateRef.current = translate;
    }
    return stableTranslateRef.current || translate;
};
